import React, {
  useState,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Alert,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import {
  Flag as ReportIcon,
} from '@mui/icons-material';

function MatchResultDialog({ open, onClose, challenge, onSubmit }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        winner_id: '',
        score: {
            participant1_score: '',
            participant2_score: '',
            details: ''
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('score.')) {
            const scoreName = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                score: {
                    ...prev.score,
                    [scoreName]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async () => {
        if (!formData.winner_id) {
            setError('Please select the winner');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Clean up score data
            const resultData = {
                winner_id: formData.winner_id,
                score: {
                    participant1_score: formData.score.participant1_score ? parseInt(formData.score.participant1_score) : null,
                    participant2_score: formData.score.participant2_score ? parseInt(formData.score.participant2_score) : null,
                    details: formData.score.details || null
                }
            };

            await onSubmit(resultData);
            handleClose();
        } catch (err) {
            console.error('Error submitting result:', err);
            setError('Failed to submit result');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            winner_id: '',
            score: {
                participant1_score: '',
                participant2_score: '',
                details: ''
            }
        });
        setError(null);
        onClose();
    };

    if (!challenge) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Report Match Result</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Game: <strong>{challenge.game_name}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {challenge.challenger.name} vs {challenge.challenged.name}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Winner Selection */}
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Who won?</FormLabel>
                        <RadioGroup
                            name="winner_id"
                            value={formData.winner_id}
                            onChange={handleChange}
                        >
                            <FormControlLabel
                                value={challenge.challenger.id}
                                control={<Radio />}
                                label={challenge.challenger.name}
                            />
                            <FormControlLabel
                                value={challenge.challenged.id}
                                control={<Radio />}
                                label={challenge.challenged.name}
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Score Fields */}
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                        Score (Optional)
                    </Typography>
                    
                    <TextField
                        fullWidth
                        type="number"
                        label={`${challenge.challenger.name}'s Score`}
                        name="score.participant1_score"
                        value={formData.score.participant1_score}
                        onChange={handleChange}
                        inputProps={{ min: 0 }}
                    />

                    <TextField
                        fullWidth
                        type="number"
                        label={`${challenge.challenged.name}'s Score`}
                        name="score.participant2_score"
                        value={formData.score.participant2_score}
                        onChange={handleChange}
                        inputProps={{ min: 0 }}
                    />

                    {/* Additional Details */}
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Match Details (optional)"
                        name="score.details"
                        value={formData.score.details}
                        onChange={handleChange}
                        placeholder="Add any additional notes about the match..."
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="success"
                    startIcon={loading ? <CircularProgress size={20} /> : <ReportIcon />}
                    disabled={loading}
                >
                    Submit Result
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MatchResultDialog;

